import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Check, Loader2, Copy, X, Share2, ExternalLink } from "lucide-react";
import { API } from "../../App";

const EMPTY = { title: "", description: "", keywords: [], og_image: "", city: "", instagram: "", facebook: "", tiktok: "", ga4_id: "", meta_pixel_id: "", google_ads_id: "" };

export function SettingsSeo({ getAuthHeaders, accent, isDark }) {
  const [seo, setSeo] = useState(EMPTY);
  const [computed, setComputed] = useState(null);
  const [urls, setUrls] = useState({});
  const [kwInput, setKwInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/seo/settings`, getAuthHeaders())
      .then((r) => {
        setSeo({ ...EMPTY, ...r.data.seo });
        setComputed(r.data.computed);
        setUrls({ vitrine: r.data.vitrine_url, share: r.data.share_url, sitemap: r.data.sitemap_url });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addKeyword = () => {
    const k = kwInput.trim().replace(/,+$/, "");
    if (!k || seo.keywords.includes(k) || seo.keywords.length >= 15) { setKwInput(""); return; }
    setSeo({ ...seo, keywords: [...seo.keywords, k] });
    setKwInput("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/seo/settings`, seo, getAuthHeaders());
      setComputed(r.data.computed);
      toast.success("Référencement mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally { setSaving(false); }
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(urls.share); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  const field = (label, key, placeholder, max) => (
    <div className="s-row" key={key}>
      <span className="s-row-label">{label}</span>
      <div style={{ flex: 1 }}>
        <input className="s-input" value={seo[key] || ""} maxLength={max}
          onChange={(e) => setSeo({ ...seo, [key]: e.target.value })} placeholder={placeholder}
          data-testid={`seo-${key}`} />
        {max && <div style={{ fontSize: 11, color: '#6B7280', textAlign: 'right', marginTop: 2 }}>{(seo[key] || "").length}/{max}</div>}
      </div>
    </div>
  );

  if (loading) return null;

  return (
    <div className="s-card" data-testid="seo-section">
      <div className="s-card-header">
        <div className="s-card-title-row">
          <Globe size={18} style={{ color: accent }} />
          <h2 className="s-card-title">Référencement (SEO) & partage</h2>
        </div>
        <button className="s-accent-btn" onClick={handleSave} disabled={saving} data-testid="seo-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Enregistrer
        </button>
      </div>

      {/* Google snippet preview */}
      {computed && (
        <div style={{ margin: '0 0 14px', padding: '12px 14px', borderRadius: 10, background: isDark ? '#0F0F0F' : '#F9FAFB', border: `1px solid ${isDark ? '#2A2A2A' : '#E5E7EB'}` }} data-testid="seo-preview">
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Aperçu Google</div>
          <div style={{ fontSize: 12, color: '#16a34a', wordBreak: 'break-all' }}>{urls.vitrine}</div>
          <div style={{ fontSize: 16, color: '#8AB4F8', fontWeight: 500, marginTop: 2 }}>{seo.title || computed.title}</div>
          <div style={{ fontSize: 13, color: isDark ? '#BDC1C6' : '#4B5563', marginTop: 2 }}>{seo.description || computed.description}</div>
        </div>
      )}

      <div className="s-rows">
        {field("Titre SEO", "title", computed?.title || "Ma boutique — Mode femme à Abidjan", 70)}
        <div className="s-row">
          <span className="s-row-label">Description</span>
          <div style={{ flex: 1 }}>
            <textarea className="s-input" rows={3} maxLength={170} value={seo.description || ""}
              onChange={(e) => setSeo({ ...seo, description: e.target.value })}
              placeholder={computed?.description || "Décrivez votre boutique en une phrase (apparaît sous le titre sur Google)"}
              data-testid="seo-description" style={{ resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: '#6B7280', textAlign: 'right', marginTop: 2 }}>{(seo.description || "").length}/170</div>
          </div>
        </div>
        <div className="s-row">
          <span className="s-row-label">Mots-clés / tags</span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {seo.keywords.map((k) => (
                <span key={k} data-testid={`seo-tag-${k}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontSize: 12, background: `${accent}18`, color: accent, border: `1px solid ${accent}40` }}>
                  {k}
                  <button onClick={() => setSeo({ ...seo, keywords: seo.keywords.filter((x) => x !== k) })} style={{ background: 'none', border: 'none', color: accent, cursor: 'pointer', display: 'flex' }} aria-label={`Retirer ${k}`}><X size={12} /></button>
                </span>
              ))}
            </div>
            <input className="s-input" value={kwInput} onChange={(e) => setKwInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addKeyword(); } }}
              onBlur={addKeyword} placeholder="Ex: robe wax, mode femme Abidjan — Entrée pour ajouter" data-testid="seo-keyword-input" />
          </div>
        </div>
        {field("Ville", "city", "Abidjan, Cocody")}
        {field("Image de partage (URL)", "og_image", "https://… (par défaut : votre logo)")}
        {field("Instagram", "instagram", "https://instagram.com/maboutique")}
        {field("Facebook", "facebook", "https://facebook.com/maboutique")}
        {field("TikTok", "tiktok", "https://tiktok.com/@maboutique")}
      </div>

      <div style={{ marginTop: 18 }}>
        <div className="s-card-title" style={{ fontSize: 13, marginBottom: 8 }}>Publicité & statistiques (optionnel)</div>
        <div className="s-rows">
          {field("Google Analytics 4", "ga4_id", "G-XXXXXXXXXX")}
          {field("Google Ads", "google_ads_id", "AW-XXXXXXXXX")}
          {field("Meta Pixel (Facebook / Instagram)", "meta_pixel_id", "123456789012345")}
        </div>
      </div>

      {/* Share link */}
      <div style={{ marginTop: 18, padding: '12px 14px', borderRadius: 10, border: `1px dashed ${accent}60`, background: `${accent}08` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Share2 size={14} style={{ color: accent }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Lien de partage WhatsApp / Facebook</span>
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 8px' }}>
          Ce lien affiche votre logo, votre titre et votre description en aperçu quand vous le partagez, puis redirige vers la vitrine.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <code style={{ flex: 1, fontSize: 11, wordBreak: 'break-all', color: isDark ? '#D1D5DB' : '#374151' }} data-testid="seo-share-url">{urls.share}</code>
          <button className="s-outlined-btn" onClick={copyShare} data-testid="seo-copy-share-btn">
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copié" : "Copier"}
          </button>
          <a className="s-outlined-btn" href={urls.share} target="_blank" rel="noreferrer" data-testid="seo-open-share-link"><ExternalLink size={12} /></a>
        </div>
        <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>Sitemap : <a href={urls.sitemap} target="_blank" rel="noreferrer" style={{ color: accent }}>{urls.sitemap}</a></div>
      </div>
    </div>
  );
}
